import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { assetInsightsApi } from '../../services/api/assetInsights.api'
import { useAuthStore } from '../../store/authStore'
import { useFeature } from '../../Utils/featureFlagEngine'
import type {
  FileInsights,
  HostInsights,
  InsightsKind,
  InsightsSubject,
  IpInsights,
} from '../../app/modules/types/assetInsights'

/**
 * The asset-insights page payload.
 *
 * A snapshot of "now", costing roughly one detail-page request. Safe to refetch
 * on focus; not something to poll.
 *
 * `retry: false` is deliberate and must not be dropped: this project's
 * queryClient defaults to `retry: 1`, and a 401/403/404 here is a real answer
 * rather than a blip. Retrying it only delays the error card that explains what
 * happened.
 */
export function useAssetInsights(kind: InsightsSubject, id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery<HostInsights | IpInsights | FileInsights>({
    queryKey: queryKeys.assetInsights.detail(orgId, kind, id ?? ''),
    queryFn: () => assetInsightsApi.getInsights(kind, id as string),
    enabled: !!id && !!orgId,
    retry: false,
  })
}

/**
 * The estate card, fired in parallel with the page payload and never blocking
 * it — the Technologies card carries its own skeleton until this lands.
 *
 * Gated on `asset_insights_tech_exposure`. The gate is on `enabled`, not just
 * on the UI: hiding the card would otherwise still pay for the request.
 */
export function useTechExposure(kind: InsightsKind, id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  const { isEnabled } = useFeature()
  const featureOn = isEnabled('asset_insights_tech_exposure')
  return useQuery({
    queryKey: queryKeys.assetInsights.techExposure(orgId, kind, id ?? ''),
    queryFn: () => assetInsightsApi.getTechExposure(kind, id as string),
    enabled: !!id && !!orgId && featureOn,
    retry: false,
  })
}
